// The key guide, generated from the bindings table and the grammar: the
// keys come from the table (so a remapped key is listed as remapped), the
// words from `ENTRIES` below, which say what each command does. The guide
// is grouped by family, and a motion or selection is listed once in its
// composed form ("] + object") beside the list of objects, not once per
// pair.

import { NOUN, phrase } from '../commands/words';
import { WAITING, type Command, type Obj } from '../commands/command';
import { commandOf, compile, keysFor, sequenceLabel, type Bindings, type Leaf, type Section } from './bindings';
import { labels } from './chord';
import { controlLabel, type ControlKeymap } from './control';

export interface GuideRow {
  /** The keys as the guide writes them (`go`, `⌘ Enter`, `] + object`). */
  keys: string[];
  /** What they do; `backticks` mark keys. */
  does: string;
  /** Where they work, when that is not the view shown. */
  note?: string;
}

export interface GuideSection {
  id: string;
  title: string;
  rows: GuideRow[];
  /** Prose shown after the lists; `backticks` mark keys. */
  notes?: string;
}

export interface GuideInput {
  bindings: Bindings;
  /** The sections live on the page as it is, innermost first. */
  live: readonly Section[];
  /** Sections of the page's other views. */
  related: readonly Section[];
  /** Keys focused controls handle themselves. */
  controls: readonly ControlKeymap<unknown>[];
  /** Whether the page has anything a command could act on; every command by default. */
  offers?: (command: Command) => boolean;
}

/** What the router does by itself on any page that registers keys. */
export const ROUTER_ROWS: readonly { keys: readonly string[]; does: string }[] = [{ keys: ['Escape'], does: 'Leave a text field or close a dialog; cancel a count or a key that waits for another' }];

/** The key that reaches a command, written into a description: the grammar's form, or with `short` the shortest (a kept alias). */
type Key = ((command: Command) => string) & { short: (command: Command) => string };
/** Whether the page has anything for a command: words about what it has not are left out. */
type Offers = (command: Command) => boolean;
/** Words that depend on the table and the page: `objects` are those with a letter that the page has something for. */
type Text = string | ((key: Key, offers: Offers, objects: readonly Obj[]) => string);

type Family = 'move' | 'select' | 'act' | 'goto' | 'present' | 'menus' | 'scroll' | 'fields';

const FAMILIES: readonly { id: Family; title: string }[] = [
  { id: 'move', title: 'Move' },
  { id: 'select', title: 'Select' },
  { id: 'act', title: 'Act on the selection, or on what the cursor is on' },
  { id: 'goto', title: 'Go to a place' },
  { id: 'present', title: 'Presentation and folds' },
  { id: 'menus', title: 'Menus and pickers' },
  { id: 'scroll', title: 'Scroll' },
  { id: 'fields', title: 'Text fields and dialogs' },
];

const SECTION_TITLE: Readonly<Record<Section, string>> = {
  field: 'comment boxes',
  guide: 'this guide',
  offer: 'the Find file offer',
  files: 'Files',
  unsent: 'Review, in the list of unsent drafts',
  send: 'Review',
  review: 'every view',
  dashboard: 'the dashboard',
  settings: 'Settings',
  grammar: 'every view',
  app: 'every page',
};

/**
 * What a command must look like to be an entry's: each field named must
 * equal the command's; a list means any of its values; null means the
 * command has no such field. An entry's keys are listed in table order.
 */
type Pattern = { kind: Command['kind'] } & Record<string, unknown>;

interface Entry {
  family: Family;
  /** The commands it describes, or the composed row of a prefix that takes an object letter. */
  is: Pattern | { perObject: 'move' | 'select' };
  /** Only rows of this section. */
  in?: Section;
  does: Text;
}

const move = (object: Obj, dir: 1 | -1, only?: 'unreviewed' | 'unresolved'): Command => (only ? { kind: 'move', object, dir, only } : { kind: 'move', object, dir });
/** A command only a page that reviews has something for: words about the reader are for such pages. */
const REVIEWS: Command = { kind: 'act', verb: 'review' };

/** Every command a key can name, in words. A test holds every row of the default table to an entry here. */
const ENTRIES: readonly Entry[] = [
  {
    family: 'move',
    is: { perObject: 'move' },
    does: (k, offers, objects) => {
      const waiting = offers(move('change', 1, 'unreviewed'))
        ? ` A capital letter stops only at what still waits for you: \`${k(move('change', 1, 'unreviewed'))}\` is the next unreviewed change, \`${k(move('thread', 1, 'unresolved'))}\` the next unresolved thread.`
        : '';
      const counted = [...objects].sort((a, b) => Number(b === 'change') - Number(a === 'change')).find((object) => offers(move(object, 1)));
      return `Next / previous object.${waiting}${counted ? ` A count goes that many: \`3${k(move(counted, 1))}\`` : ''}`;
    },
  },
  {
    family: 'move',
    is: { kind: 'move', object: 'change', only: 'unreviewed' },
    does: 'Next / previous change with unreviewed lines, the line cursor on the first of them; continues across files and wraps from the last file to the first',
  },
  { family: 'move', is: { kind: 'move', object: 'line', only: null }, in: 'files', does: 'Move the line cursor down / up' },
  { family: 'move', is: { kind: 'move', object: 'draft' }, in: 'unsent', does: 'Next / previous unsent draft; from outside the list, enter it at the ringed draft' },
  { family: 'move', is: { kind: 'move', object: 'pr' }, in: 'dashboard', does: 'Next / previous row' },
  { family: 'move', is: { kind: 'move', object: 'group' }, does: 'Next stack or section / back to the start of this one' },

  { family: 'select', is: { kind: 'range' }, does: 'Start or clear a line range at the line cursor; moving the line cursor extends it' },
  { family: 'select', is: { perObject: 'select' }, does: (k) => `Select the object around the line cursor (\`${k({ kind: 'select', object: 'change' })}\` the change, \`${k({ kind: 'select', object: 'scope' })}\` the function, impl, module or class, \`${k({ kind: 'select', object: 'file' })}\` the file): a verb then acts on it` },
  { family: 'select', is: { kind: 'grow', dir: [1, -1] }, does: 'Grow the selected lines to the enclosing change, then scope / shrink back (Alt-click ▾ selects a scope)' },

  { family: 'act', is: { kind: 'act', verb: 'review', advance: null }, does: 'Mark reviewed: the files selected in the file list, else the selection, else the change under the cursor' },
  { family: 'act', is: { kind: 'act', verb: 'review', advance: true }, does: 'Mark reviewed, then move to the next unreviewed change' },
  { family: 'act', is: { kind: 'act', verb: 'unmark' }, does: 'Remove the review mark from the files selected in the file list, else from the selection, else from the change under the cursor' },
  { family: 'act', is: { kind: 'act', verb: 'comment' }, does: 'Comment on the selected text, else the selected lines or the cursor line (else the change’s first changed line)' },
  { family: 'act', is: { kind: 'act', verb: 'edit' }, does: 'Suggest an edit to those lines; in the list of unsent drafts, edit the draft in place' },
  { family: 'act', is: { kind: 'act', verb: 'copy' }, does: 'Copy a link to those lines (to the file, when it is selected)' },
  { family: 'act', is: { kind: 'act', verb: 'discard' }, does: 'In the list of unsent drafts: discard the draft at the cursor (Undo in the notice brings it back)' },
  { family: 'act', is: { kind: 'act', verb: 'include', advance: true }, does: 'Include the draft in your review (tick it), then move to the next; on a ticked draft, take it out' },
  { family: 'act', is: { kind: 'act', verb: 'open' }, in: 'unsent', does: 'Open the draft in Files' },
  {
    family: 'act',
    is: { kind: 'act', verb: 'open' },
    in: 'dashboard',
    does: 'Open the pull request; on a run of pull requests, show or hide them. On a stack’s head: the pull request to start at (a key the head’s own button uses folds or unfolds the stack instead)',
  },

  { family: 'goto', is: { kind: 'goto', place: ['overview', 'files', 'commits', 'review'] }, does: 'Go to Overview / Files / Snapshots / Review (your unsent drafts, sending them, and the conversation)' },
  { family: 'goto', is: { kind: 'goto', place: ['top', 'end'] }, does: 'Go to the top / the end of what is shown' },
  { family: 'goto', is: { kind: 'goto', place: 'named-file' }, does: 'Find the file the selected text names (a path or file name, with :42 for a line)' },
  { family: 'goto', is: { kind: 'goto', place: 'repository' }, does: 'Go to the repository page: its pull requests' },
  { family: 'goto', is: { kind: 'goto', place: 'branches' }, does: 'Go to the repository’s branches' },
  { family: 'goto', is: { kind: 'goto', place: 'repositories' }, does: 'Go to the repository index: every repository you can review' },
  { family: 'goto', is: { kind: 'goto', place: 'history' }, does: 'Go to the history: of a pull request’s or a compare’s head branch, or this history’s commits' },

  { family: 'present', is: { kind: 'fold', object: 'change' }, does: 'Fold or unfold the change under the cursor' },
  {
    family: 'present',
    is: { kind: 'fold', to: 'toggle', object: null, level: null },
    does: (_k, offers) => (offers(REVIEWS) ? 'Fold or unfold the function, impl, module or class around the line cursor (or click ▾ beside its first line)' : 'Fold or unfold what the cursor is on'),
  },
  { family: 'present', is: { kind: 'fold', to: ['open', 'closed'], object: null, level: null }, in: 'grammar', does: 'Open / close that fold' },
  { family: 'present', is: { kind: 'fold', to: ['closed', 'open'], object: null, level: null }, in: 'dashboard', does: 'Fold / unfold the stack (or run of pull requests) at the row' },
  { family: 'present', is: { kind: 'fold', level: true }, does: 'Fold every scope at that depth (unfold them when all are folded)' },
  { family: 'present', is: { kind: 'present', what: 'centre' }, does: 'Bring the line cursor (else the change) to the middle of the reader' },
  { family: 'present', is: { kind: 'present', what: 'preview' }, does: 'Markdown and images: switch between the source and the rendered preview' },
  { family: 'present', is: { kind: 'present', what: 'file-list' }, does: 'Hide / show the file list' },
  { family: 'present', is: { kind: 'present', what: 'wrap' }, does: 'Wrap long lines, or let them scroll' },
  { family: 'present', is: { kind: 'present', what: 'layout' }, does: 'Switch between the split and the unified diff' },
  { family: 'present', is: { kind: 'present', what: 'algorithm' }, does: 'Use the next diff algorithm' },
  { family: 'present', is: { kind: 'present', what: 'minimap' }, does: 'Switch the minimap between the picture of the file and a thin strip of its changes' },

  {
    family: 'menus',
    is: { kind: 'open', menu: 'finder' },
    does: (_k, offers) => (offers(REVIEWS) ? 'In Files: find a file or search source' : 'Filter by organization, repository, author or who a pull request waits on'),
  },
  { family: 'menus', is: { kind: 'open', menu: 'stack' }, does: (k) => `In a stack: open the stack menu (the header’s “Stack 3 of 7”); \`${k({ kind: 'move', object: 'pr', dir: -1 })}\` / \`${k({ kind: 'move', object: 'pr', dir: 1 })}\` go to the pull request below (the one this builds on) / above, staying on this view` },
  { family: 'menus', is: { kind: 'open', menu: 'guide' }, does: 'Show this guide' },
  { family: 'menus', is: { kind: 'open', menu: 'feedback' }, does: 'Send feedback about Changeling' },
  { family: 'menus', is: { kind: 'open', menu: 'settings' }, does: 'Open Settings' },
  { family: 'menus', is: { kind: 'open', menu: 'jump' }, does: 'Jump to a pull request, a branch, a compare, a history or a repository' },
  { family: 'menus', is: { kind: 'open', menu: 'base' }, does: 'On a compare: pick its base (on a history, its ref), from the repository’s branches or as typed' },
  { family: 'menus', is: { kind: 'open', menu: 'head' }, does: 'On a compare: pick its head, from the repository’s branches or a fork’s (`ana:feat`), or as typed' },
  { family: 'menus', is: { kind: 'refresh' }, does: 'Refresh from GitHub' },

  { family: 'scroll', is: { kind: 'scroll', axis: 'y', by: 'line' }, does: 'Scroll down / up' },
  { family: 'scroll', is: { kind: 'scroll', axis: 'x' }, does: 'Scroll left / right when wrapping is off' },
  { family: 'scroll', is: { kind: 'scroll', by: 'half-page' }, does: 'Scroll half a page down / up' },

  { family: 'fields', is: { kind: 'submit' }, in: 'send', does: 'Publish the review: the ticked drafts, the verdict and the summary' },
  { family: 'fields', is: { kind: 'submit' }, in: 'field', does: 'Save or send the comment' },
  { family: 'fields', is: { kind: 'cancel' }, in: 'field', does: 'Close the box (in the conversation’s box, leave it)' },
  { family: 'fields', is: { kind: 'cancel' }, in: 'files', does: 'Clear the line range and selection; close the comment box and the offers for repeated lines' },
  { family: 'fields', is: { kind: 'cancel' }, in: 'guide', does: 'Close the guide' },
  { family: 'fields', is: { kind: 'cancel' }, in: 'settings', does: 'Leave Settings for the page before it' },
  { family: 'fields', is: { kind: 'cancel' }, in: 'offer', does: 'Dismiss the Find file offer' },
];

/** Prose shown after the lists while a section is listed. */
const NOTES: readonly { in: Section; text: Text }[] = [
  {
    in: 'files',
    text: (k) =>
      `Select, then act. Click any change block to make it the cursor’s. Click a line number to comment on that line; drag or shift-click line numbers to select a range. Or use the line cursor: \`${k.short(move('line', 1))}\`/\`${k.short(move('line', -1))}\` to a line, \`${k({ kind: 'range' })}\` to start a range, then \`${k({ kind: 'act', verb: 'comment' })}\` to comment or \`${k({ kind: 'act', verb: 'review' })}\` to mark it reviewed. Any line of any file takes a comment: lines outside the diff become file comments in your review, and lines of files the pull request does not change are held until you publish, then posted to the conversation. Motions cross file boundaries through the changed files, in the visible file-list order, and respect search and filters. Past the last change they wrap to the first (past the first, to the last) and say so; when \`${k.short(move('change', 1, 'unreviewed'))}\` finds no unreviewed change anywhere, it says so and stays put.`,
  },
  {
    in: 'unsent',
    text: (k) =>
      `In Review, \`${k.short({ kind: 'move', object: 'draft', dir: 1 })}\` or \`${k.short({ kind: 'move', object: 'draft', dir: -1 })}\` enters the list of unsent drafts at the ringed draft; the verbs act on that draft only while the focus is in the list, so no draft is ticked or discarded unseen. Nothing is sent unless it is ticked: a publish sends the ticked drafts and leaves the rest unsent. A key a focused button, link or checkbox uses is that control’s.`,
  },
  { in: 'dashboard', text: '`Tab` goes from the row to the next control; the list takes `Tab` once, at the row.' },
];

const matches = (is: Pattern, command: Command): boolean =>
  Object.entries(is).every(([field, want]) => {
    const has = (command as Record<string, unknown>)[field];
    if (want === null) return has === undefined;
    return Array.isArray(want) ? want.includes(has) : has === want;
  });

/** The entry that describes a row's command (a row that takes an object letter: its composed entry); undefined when none does. */
export function entryFor(leaf: Leaf): Entry | undefined {
  if ('perObject' in leaf.row) {
    const kind = leaf.row.perObject.kind;
    return ENTRIES.find((e) => 'perObject' in e.is && e.is.perObject === kind);
  }
  const command = leaf.row.command;
  return ENTRIES.find((e) => !('perObject' in e.is) && (!e.in || e.in === leaf.section) && matches(e.is, command));
}

const rank = (leaf: Leaf) => (leaf.row.alias === 'short' ? 1 : 0);

/**
 * The guide over a page: one section per family of the grammar, the
 * objects a motion or a selection takes, the keys focused controls handle
 * themselves, and last the deprecated aliases with what replaces each.
 * Every row of the table that is live on the page is listed: one the
 * entries have no words for is described from its command.
 * Sections with nothing to list are left out. A row whose keys work only
 * in a view that is not the one shown says where.
 */
export function guide(input: GuideInput): GuideSection[] {
  const { bindings, live, related, controls, offers = () => true } = input;
  const c = compile(bindings);
  const sections = [...new Set([...live, ...related])];
  if (!sections.length) return [];
  // A command no key reaches is named in words where its key would be.
  const keys = (command: Command) => {
    const here = keysFor(bindings, command, sections);
    return here.length ? here : keysFor(bindings, command);
  };
  const key: Key = Object.assign((command: Command) => keys(command)[0] ?? phrase(command), {
    short: (command: Command) => keys(command).reduce<string | null>((best, k) => (best === null || k.length < best.length ? k : best), null) ?? phrase(command),
  });
  const leaves = sections.flatMap((s) => c.leaves.get(s) ?? []);
  /** The objects the page has something for. */
  const objects = [...c.objectKeys].filter(([, hit]) => !hit.only && (offers({ kind: 'move', object: hit.object, dir: 1 }) || offers({ kind: 'select', object: hit.object })));
  const say = (t: Text) => (typeof t === 'function' ? t(key, offers, objects.map(([, hit]) => hit.object)) : t);

  const out: GuideSection[] = [];
  for (const family of FAMILIES) {
    const rows: GuideRow[] = [];
    for (const entry of ENTRIES) {
      if (entry.family !== family.id) continue;
      const mine = leaves
        .map((leaf, at) => ({ leaf, at }))
        .filter(({ leaf }) => leaf.row.alias !== 'deprecated' && entryFor(leaf) === entry)
        .filter(({ leaf }) => ('perObject' in leaf.row ? objects.some(([k]) => offers(commandOf(c, leaf, k)!)) : offers(leaf.row.command)));
      if (!mine.length) continue;
      mine.sort((a, b) => rank(a.leaf) - rank(b.leaf) || a.at - b.at);
      const keys = [...new Set(mine.map(({ leaf }) => sequenceLabel(leaf.seq) + (leaf.slot ? ' + object' : '')))];
      const where = [...new Set(mine.map(({ leaf }) => leaf.section))];
      const elsewhere = where.every((s) => !live.includes(s));
      rows.push({ keys, does: say(entry.does), ...(elsewhere ? { note: `In ${where.map((s) => SECTION_TITLE[s]).join(', ')}` } : {}) });
    }
    if (family.id === 'fields') rows.push(...ROUTER_ROWS.map((r) => ({ keys: r.keys.flatMap(labels), does: r.does })));
    if (rows.length) out.push({ id: family.id, title: family.title, rows });
    if (family.id === 'move' && objects.length && leaves.some((l) => l.slot)) {
      out.push({
        id: 'objects',
        title: 'Objects',
        rows: objects.map(([letter, hit]) => {
          const only = WAITING[hit.object];
          return { keys: [letter], does: only ? `${NOUN[hit.object]} (\`${letter.toUpperCase()}\`: ${only} only)` : NOUN[hit.object] };
        }),
      });
    }
  }
  // A row the entries have no words for (a remap to a command no default key names) is listed all the same, in the command's own words.
  const other = leaves.filter((leaf) => leaf.row.alias !== 'deprecated' && 'command' in leaf.row && !entryFor(leaf) && offers(leaf.row.command));
  if (other.length) out.push({ id: 'other', title: 'Other keys', rows: other.map((leaf) => ({ keys: [sequenceLabel(leaf.seq)], does: phrase((leaf.row as { command: Command }).command) })) });
  for (const k of controls) out.push({ id: k.id, title: k.title, rows: k.rows.map((r) => ({ keys: r.keys.map(([s]) => controlLabel(s)), does: r.does })) });

  const older: GuideRow[] = [];
  for (const leaf of leaves) {
    if (leaf.row.alias !== 'deprecated' || !('command' in leaf.row) || !offers(leaf.row.command)) continue;
    const now = keysFor(bindings, leaf.row.command, sections)[0];
    older.push({ keys: [sequenceLabel(leaf.seq)], does: now ? `${phrase(leaf.row.command)}: now \`${now}\`` : phrase(leaf.row.command) });
  }
  if (older.length) out.push({ id: 'older', title: 'Older keys, going away', rows: older });

  const notes = NOTES.filter((n) => sections.includes(n.in)).map((n) => say(n.text));
  if (notes.length && out.length) out[0] = { ...out[0], notes: notes.join(' ') };
  return out;
}
